import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import type { WaterSpec } from "../scenario/types";

/**
 * Pool water that actually looks like a pool: a refractive physical surface
 * (sky + umbrellas reflect, basin tiles show through) with a scrolling ripple
 * normal. This is NOT three.js Water.js — that object re-renders the whole
 * scene into a 512px mirror every frame, which is what pushed Chrome past
 * 2.5 GB and also tinted the water grey (it was mirroring a grey sky).
 */

function makeWaterNormals(size = 128): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return new THREE.CanvasTexture(canvas);

  const waves = [
    { fx: 2, fy: 1, amp: 1.0, phase: 0.0 },
    { fx: -3, fy: 2, amp: 0.55, phase: 1.7 },
    { fx: 5, fy: 4, amp: 0.3, phase: 3.1 },
    { fx: -7, fy: 6, amp: 0.16, phase: 0.9 },
  ];
  const heightAt = (u: number, v: number) => {
    let h = 0;
    for (const w of waves) h += w.amp * Math.sin(2 * Math.PI * (w.fx * u + w.fy * v) + w.phase);
    return h;
  };

  const img = ctx.createImageData(size, size);
  const step = 1 / size;
  const strength = 1.35;
  const n = new THREE.Vector3();
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const u = x / size;
      const v = y / size;
      const du = (heightAt(u + step, v) - heightAt(u - step, v)) / (2 * step);
      const dv = (heightAt(u, v + step) - heightAt(u, v - step)) / (2 * step);
      n.set(-du * strength, -dv * strength, size).normalize();
      const i = (y * size + x) * 4;
      img.data[i] = Math.round((n.x * 0.5 + 0.5) * 255);
      img.data[i + 1] = Math.round((n.y * 0.5 + 0.5) * 255);
      img.data[i + 2] = Math.round((n.z * 0.5 + 0.5) * 255);
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.NoColorSpace;
  tex.repeat.set(6, 9);
  return tex;
}

/** Light mosaic + a dark lane stripe down the long axis, matching the Unity pack shots. */
function makePoolFloorTexture(size = 256, lane = true): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return new THREE.CanvasTexture(canvas);

  const tiles = 16;
  const tile = size / tiles;
  ctx.fillStyle = "#d7e4ea";
  ctx.fillRect(0, 0, size, size);
  for (let y = 0; y < tiles; y += 1) {
    for (let x = 0; x < tiles; x += 1) {
      const shade = 210 + ((x * 13 + y * 7) % 18);
      ctx.fillStyle = `rgb(${shade - 8}, ${shade + 4}, ${shade + 10})`;
      ctx.fillRect(x * tile + 1, y * tile + 1, tile - 2, tile - 2);
    }
  }
  // Centre lane marker (navy), two tiles wide — lap pool only.
  if (lane) {
    ctx.fillStyle = "#1a3350";
    ctx.fillRect(size * 0.5 - tile, 0, tile * 2, size);
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  tex.repeat.set(6, 10);
  return tex;
}

export function WaterSurface({ spec }: { spec: WaterSpec }) {
  const circle = spec.shape === "circle";
  const normals = useMemo(() => makeWaterNormals(), []);
  const floorTex = useMemo(() => makePoolFloorTexture(256, !circle), [circle]);
  const speed = spec.speed ?? 1;
  const normalScale = useRef(new THREE.Vector2(0.48, 0.48));
  const dist = spec.distortionScale;
  if (typeof dist === "number" && dist > 0 && dist < 4) {
    // Older specs used Water.js distortion (~2.2). Physical-water normalScale
    // lives around 0.4–0.7; values above 1.5 are treated as the old unit.
    const n = dist > 1.5 ? 0.5 : dist;
    normalScale.current.set(n, n);
  }

  useFrame((_, delta) => {
    normals.offset.x += delta * speed * 0.028;
    normals.offset.y += delta * speed * 0.017;
  });

  useEffect(() => {
    return () => {
      normals.dispose();
      floorTex.dispose();
    };
  }, [normals, floorTex]);

  const [cx, cy, cz] = spec.center;
  const floorY = spec.floorY ?? cy - (circle ? 0.65 : 1.55);
  const waterMat = (
        <meshPhysicalMaterial
          color={spec.color ?? "#1478a8"}
          roughness={0.06}
          metalness={0}
          transmission={0.86}
          thickness={circle ? 1.1 : 2.2}
          ior={1.333}
          attenuationColor="#0a4a6e"
          attenuationDistance={circle ? 2.4 : 4.2}
          transparent
          opacity={0.98}
          envMapIntensity={1.55}
          normalMap={normals}
          normalScale={normalScale.current}
          side={THREE.FrontSide}
        />
  );
  const radius = spec.width / 2;

  return (
    <group name="pool_water_group">
      <mesh
        name="pool_floor"
        rotation={[-Math.PI / 2, 0, 0]}
        position={[cx, floorY, cz]}
        receiveShadow
      >
        {circle ? (
          <circleGeometry args={[radius * 0.96, 48]} />
        ) : (
          <planeGeometry args={[spec.width * 0.98, spec.depth * 0.98]} />
        )}
        <meshStandardMaterial map={floorTex} roughness={0.28} metalness={0} />
      </mesh>
      <mesh
        name="pool_water"
        rotation={[-Math.PI / 2, 0, 0]}
        position={[cx, cy, cz]}
        renderOrder={2}
      >
        {circle ? (
          <circleGeometry args={[radius, 48]} />
        ) : (
          <planeGeometry args={[spec.width, spec.depth]} />
        )}
        {waterMat}
      </mesh>
    </group>
  );
}
